import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PreviewIcon from '@mui/icons-material/Preview';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import { importarPreciosArticuloExcel } from '../../services/preciosArticuloService';
import type { PreciosProveedorImportResult } from '../../services/preciosProveedorService';
import SearchableSelect from '../../components/SearchableSelect';
import {
  EXCEL_IMPORT_SIZE_HELPER_TEXT,
  getExcelFileSizeStatusText,
  getExcelFileTooLargeMessage,
  isExcelImportTooLarge,
  resolveImportExcelError,
} from '../../utils/importExcelLimits';
import PreciosImportPreviewDialog from './PreciosImportPreviewDialog';
import {
  buildPreciosImportPreviewArticulo,
  crearProductoPreviewCache,
  PreciosImportPreviewRow,
  ProductoPreviewCache,
} from '../../utils/buildPreciosImportPreview';
import {
  applyPreviewEditsToWorkbook,
  clonarWorkbook,
  dedupePreciosImportWorkbookLastWins,
  findCodigosDuplicadosPreciosImport,
  formatCodigosDuplicadosMensaje,
  necesitaAdvertenciaDuplicadosPreciosImport,
  parseExcelPreciosImportRows,
  workbookToXlsxFile,
} from '../../utils/preciosImportExcelParse';
import {
  buildPreviewColumnDefsArticulo,
  columnDefsToExcelColumnMap,
} from '../../utils/preciosImportPreviewColumns';

const ImportacionPreciosArticulo: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [hojasDisponibles, setHojasDisponibles] = useState<string[]>([]);
  const [nombreHoja, setNombreHoja] = useState('');
  const [primeraFilaDatos, setPrimeraFilaDatos] = useState('2');
  const [ultimaFilaDatos, setUltimaFilaDatos] = useState('');
  const [colCodigo, setColCodigo] = useState('B');
  const [colCosto, setColCosto] = useState('G');
  const [colPrecioNeto, setColPrecioNeto] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<PreciosProveedorImportResult | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFilas, setPreviewFilas] = useState<PreciosImportPreviewRow[]>([]);
  const [previewSubtitulo, setPreviewSubtitulo] = useState('');
  const productoCacheRef = useRef<ProductoPreviewCache>(crearProductoPreviewCache());
  const workbookOriginalRef = useRef<XLSX.WorkBook | null>(null);

  const archivoExcedeLimite = isExcelImportTooLarge(archivo);

  const previewColumnas = useMemo(
    () =>
      buildPreviewColumnDefsArticulo({
        columnaCosto: colCosto,
        columnaPrecioNeto: colPrecioNeto,
      }),
    [colCosto, colPrecioNeto]
  );

  const invalidarProductoCache = () => {
    productoCacheRef.current = crearProductoPreviewCache();
  };

  const invalidarWorkbook = () => {
    workbookOriginalRef.current = null;
  };

  const crearArchivoConEdicionesPreview = (): File | null => {
    const wbOriginal = workbookOriginalRef.current;
    if (!wbOriginal || !archivo) return null;
    const hoja = nombreHoja.trim();
    if (!hoja) return null;

    const wb = clonarWorkbook(wbOriginal);
    applyPreviewEditsToWorkbook(
      wb,
      {
        nombreHoja: hoja,
        ...columnDefsToExcelColumnMap(previewColumnas),
      },
      previewFilas
    );
    const baseName = archivo.name.replace(/\.(xlsx|xlsm)$/i, '');
    return workbookToXlsxFile(wb, `${baseName}-editado.xlsx`);
  };

  const armarPreview = async (
    wb: XLSX.WorkBook,
    res: PreciosProveedorImportResult,
    soloSimular: boolean,
    opciones?: { preguntarAntesDeAbrir?: boolean }
  ) => {
    const hoja = nombreHoja.trim();
    const colCod = colCodigo.trim();
    const colCost = colCosto.trim();
    const colPrecio = colPrecioNeto.trim();
    const primera = Math.max(1, parseIntOr(primeraFilaDatos, 1));
    const ultimaTrim = ultimaFilaDatos.trim();
    const ultima =
      ultimaTrim !== ''
        ? parseIntOr(ultimaTrim, NaN)
        : undefined;

    const filasExcel = parseExcelPreciosImportRows({
      workbook: wb,
      nombreHoja: hoja,
      primeraFilaDatos: primera,
      filasLeidasApi: res.filasLeidas,
      ...(ultima !== undefined && Number.isFinite(ultima) ? { ultimaFilaDatos: ultima } : {}),
      columnaCodigo: colCod,
      ...(colCost ? { columnaCosto: colCost } : {}),
      ...(colPrecio ? { columnaPrecioNeto: colPrecio } : {}),
    });
    const preview = await buildPreciosImportPreviewArticulo(filasExcel, res, {
      productoCache: productoCacheRef.current,
    });
    setPreviewFilas(preview);
    setPreviewSubtitulo(
      soloSimular
        ? 'Simulación — puede editar costo y precio neto antes de procesar'
        : 'Proceso finalizado — cambios aplicados'
    );
    if (soloSimular) {
      if (opciones?.preguntarAntesDeAbrir) {
        if (window.confirm('¿Desea abrir la previsualización de los cambios?')) {
          setPreviewOpen(true);
        }
      } else {
        setPreviewOpen(true);
      }
    } else {
      setPreviewOpen(false);
    }
  };

  const parseIntOr = (s: string, fallback: number) => {
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const ejecutar = async (
    soloSimular: boolean,
    archivoOverride?: File,
    opciones?: { preguntarAbrirPreview?: boolean }
  ) => {
    let archivoAUsar = archivoOverride ?? archivo;
    if (!archivoAUsar) {
      enqueueSnackbar('Seleccione un archivo Excel (.xlsx).', { variant: 'warning' });
      return;
    }
    const hoja = nombreHoja.trim();
    if (!hoja) {
      enqueueSnackbar('Seleccione la hoja del libro.', { variant: 'warning' });
      return;
    }
    if (isExcelImportTooLarge(archivoAUsar)) {
      enqueueSnackbar(getExcelFileTooLargeMessage(archivoAUsar), { variant: 'error' });
      return;
    }

    const colCod = colCodigo.trim();
    const colCost = colCosto.trim();
    const colPrecio = colPrecioNeto.trim();
    if (!colCod) {
      enqueueSnackbar('Indique la columna del código de artículo (Stock).', { variant: 'warning' });
      return;
    }
    if (!colCost) {
      enqueueSnackbar('Indique la columna de costo base.', { variant: 'warning' });
      return;
    }
    const primera = Math.max(1, parseIntOr(primeraFilaDatos, 1));
    const ultimaTrim = ultimaFilaDatos.trim();
    let ultima: number | undefined;
    if (ultimaTrim !== '') {
      ultima = parseIntOr(ultimaTrim, NaN);
      if (!Number.isFinite(ultima) || ultima < 1) {
        enqueueSnackbar('"Hasta fila" debe ser un número entero ≥ 1 o dejarse vacío.', { variant: 'warning' });
        return;
      }
      if (ultima < primera) {
        enqueueSnackbar('"Hasta fila" no puede ser menor que la primera fila de datos.', { variant: 'warning' });
        return;
      }
    }

    let wbParaApi: XLSX.WorkBook;
    try {
      if (archivoOverride) {
        const ab = await archivoOverride.arrayBuffer();
        wbParaApi = XLSX.read(ab, { type: 'array' });
      } else {
        let wbCached = workbookOriginalRef.current;
        if (!wbCached) {
          const ab = await archivoAUsar.arrayBuffer();
          wbCached = XLSX.read(ab, { type: 'array' });
          workbookOriginalRef.current = wbCached;
        }
        wbParaApi = clonarWorkbook(wbCached);
      }
    } catch (e) {
      console.error('Error al leer Excel para detectar duplicados', e);
      enqueueSnackbar('No se pudo leer el archivo Excel.', { variant: 'error' });
      return;
    }

    const filasExcelDup = parseExcelPreciosImportRows({
      workbook: wbParaApi,
      nombreHoja: hoja,
      primeraFilaDatos: primera,
      ...(ultima !== undefined ? { ultimaFilaDatos: ultima } : {}),
      columnaCodigo: colCod,
      columnaCosto: colCost,
      ...(colPrecio ? { columnaPrecioNeto: colPrecio } : {}),
    });
    const codigosDuplicados = findCodigosDuplicadosPreciosImport(filasExcelDup);
    if (necesitaAdvertenciaDuplicadosPreciosImport(soloSimular, codigosDuplicados)) {
      const lista = formatCodigosDuplicadosMensaje(codigosDuplicados);
      const ok = window.confirm(
        `Hay códigos duplicados: ${lista}. Si continúa, se usará el último precio de cada código. ¿Continuar?`
      );
      if (!ok) return;
      dedupePreciosImportWorkbookLastWins(
        wbParaApi,
        {
          nombreHoja: hoja,
          columnaCodigo: colCod,
          columnaCosto: colCost,
          ...(colPrecio ? { columnaPrecioNeto: colPrecio } : {}),
        },
        filasExcelDup
      );
      const baseName = archivoAUsar.name.replace(/\.(xlsx|xlsm)$/i, '');
      archivoAUsar = workbookToXlsxFile(wbParaApi, `${baseName}-sin-duplicados.xlsx`);
    }

    setLoading(true);
    setResultado(null);
    try {
      const res = await importarPreciosArticuloExcel(archivoAUsar, {
        nombreHoja: hoja,
        primeraFilaDatos: primera,
        ...(ultima !== undefined ? { ultimaFilaDatos: ultima } : {}),
        columnaCodigoArticulo: colCod,
        columnaCosto: colCost,
        ...(colPrecio ? { columnaPrecioNetoVenta: colPrecio } : {}),
        dryRun: soloSimular,
        guardarHistorico: !soloSimular,
      });
      setResultado(res);

      try {
        await armarPreview(wbParaApi, res, soloSimular, {
          preguntarAntesDeAbrir: opciones?.preguntarAbrirPreview ?? false,
        });
      } catch (previewErr) {
        console.error('Error al armar previsualización de precios', previewErr);
        enqueueSnackbar(
          'La importación se procesó, pero no se pudo armar la previsualización detallada.',
          { variant: 'warning' }
        );
      }

      if (!soloSimular) {
        invalidarProductoCache();
      }

      if (res.advertencias.length > 0) {
        res.advertencias.forEach((a) => enqueueSnackbar(a, { variant: 'info' }));
      }
      enqueueSnackbar(
        soloSimular
          ? `Simulación: ${res.costosPorProveedorActualizados} CPP / ${res.stockRecalculados} stock.`
          : `Proceso finalizado: ${res.costosPorProveedorActualizados} CPP actualizados.`,
        { variant: 'success' }
      );
    } catch (e: unknown) {
      const msg = resolveImportExcelError(e, archivoAUsar, 'Error al importar precios por artículo.');
      console.error('Importación costos Base por código de artículo', e);
      enqueueSnackbar(msg.length > 800 ? `${msg.slice(0, 800)}…` : msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const reSimularDesdePreview = async () => {
    const archivoEditado = crearArchivoConEdicionesPreview();
    if (!archivoEditado) {
      enqueueSnackbar('No hay archivo cargado para re-simular.', { variant: 'warning' });
      return;
    }
    await ejecutar(true, archivoEditado);
  };

  const procesarDesdePreview = async () => {
    const archivoEditado = crearArchivoConEdicionesPreview();
    if (!archivoEditado) {
      enqueueSnackbar('No hay archivo cargado para procesar.', { variant: 'warning' });
      return;
    }
    await ejecutar(false, archivoEditado);
  };

  const cardSx = {
    bgcolor: '#ffffff',
    borderRadius: 3,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'hidden' as const,
  };

  const etiquetaSinCoincidencia = (d: PreciosProveedorImportResult['sinCoincidenciaDetalle'][0]) =>
    d.codigoArticulo ?? d.codigoArticuloProveedor;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Card sx={cardSx}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/inventario/mantenimiento-precios')}
                  sx={{ textTransform: 'none' }}
                >
                  Mantenimiento de precios
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Actualización de Costos Base por código de artículo desde Excel
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Busca por <strong>Codigo de Articulo</strong> en el excel. Debe indicar la columna de{' '}
                <strong>Costo Base</strong> (obligatoria). Opcionalmente puede indicar la de{' '}
                <strong>Precio Neto de Venta</strong>; si no la indica, el precio se recalcula según el % de ganancia
                del artículo.
              </Typography>

              <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
                {EXCEL_IMPORT_SIZE_HELPER_TEXT}
              </Alert>

              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                {archivo ? archivo.name : 'Elegir archivo .xlsx'}
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xlsm"
                  onChange={async (e) => {
                    const input = e.target;
                    const f = input.files?.[0] ?? null;
                    setArchivo(f);
                    setHojasDisponibles([]);
                    setNombreHoja('');
                    setResultado(null);
                    invalidarProductoCache();
                    invalidarWorkbook();
                    if (!f) {
                      input.value = '';
                      return;
                    }
                    if (isExcelImportTooLarge(f)) {
                      enqueueSnackbar(getExcelFileTooLargeMessage(f), { variant: 'error' });
                      input.value = '';
                      return;
                    }
                    try {
                      const ab = await f.arrayBuffer();
                      const wb = XLSX.read(ab, { type: 'array' });
                      workbookOriginalRef.current = wb;
                      const names = wb.SheetNames ?? [];
                      if (names.length === 0) {
                        enqueueSnackbar('El archivo no contiene hojas reconocibles.', { variant: 'warning' });
                        setArchivo(null);
                        input.value = '';
                        return;
                      }
                      setHojasDisponibles(names);
                      setNombreHoja(names[0]);
                    } catch (err) {
                      console.error('Lectura de hojas Excel', err);
                      enqueueSnackbar(
                        'No se pudo leer el archivo Excel. Verifique que sea .xlsx o .xlsm válido.',
                        { variant: 'error' }
                      );
                      setArchivo(null);
                      input.value = '';
                    }
                    input.value = '';
                  }}
                />
              </Button>
              {archivo && (
                <Typography variant="body2" color={archivoExcedeLimite ? 'error' : 'text.secondary'}>
                  {getExcelFileSizeStatusText(archivo)}
                </Typography>
              )}
              {archivoExcedeLimite && archivo && (
                <Alert severity="error">{getExcelFileTooLargeMessage(archivo)}</Alert>
              )}

              <SearchableSelect
                label="Hoja"
                disabled={hojasDisponibles.length === 0 || archivoExcedeLimite}
                options={hojasDisponibles.map((n) => ({ value: n, label: n }))}
                value={hojasDisponibles.includes(nombreHoja) ? nombreHoja : null}
                onChange={(value) => setNombreHoja(value ?? '')}
                helperText={
                  hojasDisponibles.length === 0
                    ? 'Elija un archivo para listar las hojas del libro.'
                    : 'Hojas detectadas en el archivo seleccionado.'
                }
              />
              <TextField
                label="Número de la primera fila de datos"
                value={primeraFilaDatos}
                onChange={(e) => setPrimeraFilaDatos(e.target.value)}
                type="number"
                fullWidth
              />
              <TextField
                label="Hasta fila (última fila Excel a procesar)"
                value={ultimaFilaDatos}
                onChange={(e) => setUltimaFilaDatos(e.target.value)}
                type="number"
                fullWidth
                helperText="Opcional. Vacío = hasta el final del archivo. Inclusive (misma numeración que Excel)."
              />
              <TextField
                label="Letra de la columna que contiene el Codigo de Articulo"
                value={colCodigo}
                onChange={(e) => setColCodigo(e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: B o AB (columna Excel)"
              />
              <TextField
                required
                label="Letra de la columna Costo Base"
                value={colCosto}
                onChange={(e) => setColCosto(e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: G (actualiza CPP y costo catálogo si la celda tiene valor)"
                helperText="Obligatoria. Si la celda de la fila tiene valor, actualiza el costo base del artículo."
              />
              <TextField
                label="Letra de la columna Precio Neto de Venta"
                value={colPrecioNeto}
                onChange={(e) => setColPrecioNeto(e.target.value.toUpperCase())}
                fullWidth
                placeholder="Ej: H (opcional; actualiza PrecioSinIva si la celda tiene valor)"
                helperText="Opcional. Si se indica y la celda de la fila tiene valor, se usa ese precio neto en lugar del calculado por % de ganancia."
              />

              <FormControlLabel
                control={<Checkbox checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
                label="Solo simular: no realiza cambios en la base de datos"
              />
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label="Guardar snapshot en CostosPorProveedorHistorico antes de realizar cambios"
              />

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PreviewIcon />}
                  disabled={loading || archivoExcedeLimite}
                  onClick={() => ejecutar(true, undefined, { preguntarAbrirPreview: true })}
                >
                  Simular
                </Button>
                <Button
                  variant="contained"
                  disabled={loading || dryRun || archivoExcedeLimite}
                  onClick={() => ejecutar(false)}
                >
                  Procesar
                </Button>
              </Stack>
              {dryRun && (
                <Typography variant="caption" color="text.secondary">
                  Desactive &quot;Solo simular&quot; para habilitar Procesar.
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Card>

        {resultado && (
          <Card sx={cardSx}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  Resultado {resultado.dryRun ? '(simulación)' : ''}
                </Typography>
                {previewFilas.length > 0 && (
                  <Button variant="outlined" size="small" startIcon={<PreviewIcon />} onClick={() => setPreviewOpen(true)}>
                    {resultado.dryRun ? 'Ver previsualización' : 'Ver detalle de cambios aplicados'}
                  </Button>
                )}
              </Stack>
              <Stack spacing={1}>
                <Typography variant="body2">Filas leídas: {resultado.filasLeidas}</Typography>
                <Typography variant="body2">
                  CPP actualizados: {resultado.costosPorProveedorActualizados}
                </Typography>
                {(resultado.costosPorProveedorCreadosDesdeStock ?? 0) > 0 && (
                  <Typography variant="body2">
                    CPP nuevos (desde Stock): {resultado.costosPorProveedorCreadosDesdeStock}
                  </Typography>
                )}
                <Typography variant="body2">
                  Costos Base de Articulos actualizados: {resultado.stockRecalculados}
                </Typography>
                {(resultado.preciosNetoVentaActualizados ?? 0) > 0 && (
                  <Typography variant="body2">
                    Precios netos de venta desde Excel: {resultado.preciosNetoVentaActualizados}
                  </Typography>
                )}
                <Typography variant="body2">
                  Código de Articulo no encontrado: {resultado.sinCoincidenciaCostosPorProveedor}
                </Typography>
                {(resultado.filasSinProveedorPrincipal ?? 0) > 0 && (
                  <Typography variant="body2">
                    Filas con artículo sin proveedor principal (no actualiza CPP): {resultado.filasSinProveedorPrincipal}
                  </Typography>
                )}
                {(resultado.stockActualizadosSinProveedorPrincipal ?? 0) > 0 && (
                  <Typography variant="body2">
                    Stock actualizados por costo Excel (sin CPP): {resultado.stockActualizadosSinProveedorPrincipal}
                  </Typography>
                )}
                <Typography variant="body2">Filas inválidas: {resultado.filasInvalidas}</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  «Código de Articulo no encontrado» solo aplica cuando no hay ningun producto con ese Código. Si hay
                  filas sin Proveedor Principal, no se actualiza CostosPorProveedor; al procesar (no simular) el costo del Excel
                  se aplica a StockDescuentos (costo catálogo) y a Stock (costo y precio de venta) con la misma lógica que tras
                  un cambio por CPP.
                </Typography>
              </Stack>
              {resultado.erroresFila.length > 0 && (
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {resultado.erroresFila.slice(0, 50).map((e) => (
                    <li key={e}>
                      <Typography variant="caption">{e}</Typography>
                    </li>
                  ))}
                  {resultado.erroresFila.length > 50 && (
                    <Typography variant="caption">… y más</Typography>
                  )}
                </Box>
              )}
              {resultado.sinCoincidenciaDetalle.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Códigos de Articulo inexistentes ({Math.min(20, resultado.sinCoincidenciaDetalle.length)} de{' '}
                    {resultado.sinCoincidenciaDetalle.length}):
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {resultado.sinCoincidenciaDetalle.slice(0, 20).map((d) => (
                      <li key={`${d.filaExcel}-${etiquetaSinCoincidencia(d)}`}>
                        <Typography variant="caption">
                          Fila {d.filaExcel}: {etiquetaSinCoincidencia(d)}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Stack>

      <PreciosImportPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        filas={previewFilas}
        columnas={previewColumnas}
        onFilasChange={setPreviewFilas}
        subtitulo={previewSubtitulo}
        mostrarDescripcion
        editable={!!resultado?.dryRun}
        procesado={resultado?.dryRun === false}
        puedeEjecutarDesdePreview={!!resultado?.dryRun}
        onReSimular={reSimularDesdePreview}
        onProcesar={procesarDesdePreview}
        ejecutando={loading}
      />
    </Box>
  );
};

export default ImportacionPreciosArticulo;
